import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Save, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const AREAS = [["foundations", "Foundations"], ["lights", "One Million Lights"], ["soulumo", "Soulumo"]];
const input = "w-full rounded-lg border border-[#d9d4c7] bg-white px-3 py-2 text-sm text-[#2b2b2b] outline-none focus:border-[#4A6741]";
const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || `q_${Date.now()}`;

const FaqRow = ({ f, i, resets, set, remove }) => (
  <div className="rounded-xl border border-[#e5e0d3] bg-[#fbfaf6] p-4 space-y-2" data-testid={`guide-admin-faq-${i}`}>
    <div className="flex gap-2">
      <input className={input} value={f.q} onChange={(e) => set({ ...f, q: e.target.value })} placeholder="Question" data-testid={`guide-admin-q-${i}`} />
      <button type="button" onClick={remove} className="text-[#a0522d] px-2" aria-label="Remove" data-testid={`guide-admin-remove-${i}`}><Trash2 size={16} /></button>
    </div>
    <textarea className={input} rows={3} value={f.a} onChange={(e) => set({ ...f, a: e.target.value })} placeholder="Spoken answer" data-testid={`guide-admin-a-${i}`} />
    <input className={input} value={(f.k || []).join(", ")} onChange={(e) => set({ ...f, k: e.target.value.split(",").map((x) => x.trim()) })} placeholder="Match words, comma separated (e.g. sleep, night, bed)" data-testid={`guide-admin-k-${i}`} />
    <div className="grid grid-cols-3 gap-2">
      <input className={input} value={f.link?.label || ""} onChange={(e) => set({ ...f, link: { label: e.target.value, route: f.link?.route || "/" } })} placeholder="Button label" data-testid={`guide-admin-link-label-${i}`} />
      <input className={input} value={f.link?.route || ""} onChange={(e) => set({ ...f, link: { label: f.link?.label || "Open", route: e.target.value } })} placeholder="/page" data-testid={`guide-admin-link-route-${i}`} />
      <select className={input} value={f.reset || ""} onChange={(e) => set({ ...f, reset: e.target.value || undefined })} data-testid={`guide-admin-reset-${i}`}>
        <option value="">No reset</option>
        {resets.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    </div>
  </div>
);

const LABELS = { open: "Guide opened", question: "Questions tapped", typed: "Typed questions answered", miss: "Typed, no answer", reset: "Resets played",
  checkin_morning: "Morning check-ins", checkin_evening: "Evening check-ins", mood: "Moods chosen", intro: "First-visit explanations" };

const Usage = ({ u, faqs }) => {
  const name = (t) => (t.type === "question" ? faqs.find((f) => f.id === t.id)?.q || t.id : t.type === "mood" ? `Mood: ${t.id.replace("_", " · ")}` : `Reset: ${t.id}`);
  return (
    <section className="mt-8 rounded-2xl bg-white p-5 border border-[#e5e0d3]" data-testid="guide-admin-usage">
      <h2 className="text-lg text-[#2b2b2b]">Usage · last {u.days} days</h2>
      {!Object.keys(u.totals).length ? <p className="mt-2 text-sm text-[#6b6b6b]">No use yet.</p> : (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(LABELS).filter(([k]) => u.totals[k]).map(([k, l]) => (
              <div key={k} className="rounded-xl bg-[#f6f3ec] p-3" data-testid={`guide-admin-usage-${k}`}>
                <p className="text-2xl text-[#2b2b2b]">{u.totals[k]}</p><p className="text-xs text-[#6b6b6b]">{l}</p>
              </div>
            ))}
          </div>
          {!!u.top.length && (
            <ul className="mt-4 text-sm divide-y divide-[#eee9dc]" data-testid="guide-admin-usage-top">
              {u.top.map((t) => <li key={`${t.type}-${t.id}`} className="py-1.5 flex justify-between"><span className="text-[#2b2b2b]">{name(t)}</span><span className="text-[#6b6b6b]">{t.count}</span></li>)}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

// Admin: edit the ready-made guide answers (re-voiced on save) and see questions with no answer yet.
export default function AdminGuides() {
  const [area, setArea] = useState("foundations");
  const [d, setD] = useState(null);
  const [state, setState] = useState("");
  const load = () => { setD(null); axios.get(`${API}/api/admin/guides/${area}`).then((r) => setD(r.data)).catch(() => setState("Couldn't load")); };
  useEffect(load, [area]); // eslint-disable-line react-hooks/exhaustive-deps
  const setFaq = (i, f) => setD({ ...d, faqs: d.faqs.map((x, j) => (j === i ? f : x)) });
  const add = (q = "") => setD({ ...d, faqs: [...d.faqs, { id: slug(q || `new ${d.faqs.length + 1}`), q, a: "", k: [] }] });
  const save = async () => {
    setState("Saving…");
    const faqs = d.faqs.map((f) => ({ ...f, id: f.id || slug(f.q), k: (f.k || []).filter(Boolean), link: f.link?.route ? f.link : null }));
    try { await axios.put(`${API}/api/admin/guides/${area}`, { welcome: d.welcome, fallback: d.fallback, faqs }); setState("Saved — new answers are being voiced now"); load(); }
    catch (e) {
      const det = e?.response?.data?.detail;
      const first = Array.isArray(det) ? det[0] : null;
      const row = first?.loc?.includes("faqs") ? `Question ${Number(first.loc[first.loc.indexOf("faqs") + 1]) + 1}: ` : "";
      const field = first?.loc?.slice(-1)[0];
      const why = first ? `${row}${{ q: "the question", a: "the answer", route: "the button page", welcome: "the welcome line", fallback: "the no-match line" }[field] || field} ${first.type === "string_too_short" ? "is too short" : first.type === "string_too_long" ? "is too long" : "isn't valid"}` : det === "duplicate_ids" ? "two questions share the same id" : "please try again";
      setState(`Not saved: ${why}.`);
    }
  };
  const resolve = async (m) => { await axios.post(`${API}/api/admin/guides/${area}/misses/resolve`, { text: m.text }); load(); };

  return (
    <div className="min-h-screen bg-[#f6f3ec] px-4 pt-10 pb-32" data-testid="admin-guides-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl text-[#2b2b2b]" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Guide answers</h1>
        <p className="mt-1 text-sm text-[#6b6b6b]">Ready-made guides: no AI per question. Saving re-voices any changed line in both voices.</p>
        <div className="mt-6 flex gap-2">
          {AREAS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setArea(k)} data-testid={`guide-admin-area-${k}`}
              className={`rounded-full px-4 py-2 text-sm ${area === k ? "bg-[#4A6741] text-white" : "bg-white text-[#2b2b2b] border border-[#e5e0d3]"}`}>{l}</button>
          ))}
        </div>
        {!d ? <p className="mt-8 text-sm text-[#6b6b6b]">{state || "Loading…"}</p> : (
          <>
            {d.usage && <Usage u={d.usage} faqs={d.faqs} />}
            <section className="mt-8 rounded-2xl bg-white p-5 border border-[#e5e0d3]" data-testid="guide-admin-misses">
              <h2 className="text-lg text-[#2b2b2b]">Asked, but no answer yet</h2>
              {!d.misses.length ? <p className="mt-2 text-sm text-[#6b6b6b]">Nothing yet. Questions the guide couldn't match will appear here.</p> : (
                <ul className="mt-3 divide-y divide-[#eee9dc]">
                  {d.misses.map((m) => (
                    <li key={m.norm} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`guide-admin-miss-${m.norm.replace(/\s+/g, "-").slice(0, 30)}`}>
                      <span className="text-[#2b2b2b]">“{m.text}” <span className="text-[#6b6b6b]">· asked {m.count}×</span></span>
                      <span className="flex gap-3 shrink-0">
                        <button type="button" onClick={() => add(m.text)} className="text-[#4A6741] inline-flex items-center gap-1" data-testid="guide-admin-miss-add"><Plus size={13} /> Answer it</button>
                        <button type="button" onClick={() => resolve(m)} className="text-[#6b6b6b] inline-flex items-center gap-1" data-testid="guide-admin-miss-dismiss"><Check size={13} /> Dismiss</button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="mt-6 space-y-3">
              <label className="block text-sm text-[#2b2b2b]">Welcome line
                <textarea className={`${input} mt-1`} rows={2} value={d.welcome} onChange={(e) => setD({ ...d, welcome: e.target.value })} data-testid="guide-admin-welcome" />
              </label>
              <label className="block text-sm text-[#2b2b2b]">When there's no match
                <textarea className={`${input} mt-1`} rows={2} value={d.fallback} onChange={(e) => setD({ ...d, fallback: e.target.value })} data-testid="guide-admin-fallback" />
              </label>
            </section>
            <section className="mt-6 space-y-3">
              {d.faqs.map((f, i) => <FaqRow key={`${f.id}-${i}`} f={f} i={i} resets={d.resets} set={(x) => setFaq(i, x)} remove={() => setD({ ...d, faqs: d.faqs.filter((_, j) => j !== i) })} />)}
              <button type="button" onClick={() => add()} className="inline-flex items-center gap-2 text-sm text-[#4A6741]" data-testid="guide-admin-add"><Plus size={14} /> Add a question</button>
            </section>
            <div className="sticky bottom-24 mt-8 flex items-center gap-4">
              <button type="button" onClick={save} className="inline-flex items-center gap-2 rounded-full bg-[#4A6741] px-6 py-3 text-sm text-white shadow-lg" data-testid="guide-admin-save"><Save size={15} /> Save &amp; re-voice</button>
              {state && <span className="text-sm text-[#2b2b2b]" data-testid="guide-admin-status">{state}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
